<template>
    <ul class="layout-menu">
        <div class="tw:flex tw:justify-between tw:items-center tw:-mb-4">
            <p class="tw:font-semibold tw:text-xl tw:uppercase tw:pl-4">Menu</p>
            <Button
                class="tw:pr-4 tw:bg-transparent tw:border-none tw:text-[var(--text-color)]"
                icon="uil-multiply"
                size="large"
                @click="toggleMenu"
            />
        </div>
        <Divider class="tw:-mb-2" />
        <template v-for="(item, i) in model" :key="item">
            <app-menu-item v-if="!item.separator" :item="item" :index="i" />
            <li v-if="item.separator" class="menu-separator" />
        </template>
    </ul>
</template>

<script setup>
import intersection from 'lodash/intersection.js';
import { computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useCanSeeAdministrationDropdown } from '@/composables/useCanSeeAdministrationDropdown.js';
import { useLayout } from '@/layouts/composables/layout.js';
import Page from '@/models/pages/Page.js';
import User from '@/models/User.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import { gate } from '@/plugins/vue-gate/Index.js';
import { canViewInbox } from '@/policies/InboxPolicy.js';
import {
    canManageLateSubmissionPolicies,
    canViewLateSubmissionDeductionsReport,
} from '@/policies/LateSubmissionPolicyPolicy.js';
import AppMenuItem from './AppMenuItem.vue';

const { toggleMenu } = useLayout();
const store = useStore();
const instance = getCurrentInstance();
const { $hasFeature } = instance.appContext.config.globalProperties;

// Computed properties
const user = computed(() => {
    return User.query().with(Gate.userRelationships()).find(store.state.Auth.user?.id);
});

const canSeeCourseDropdown = computed(() => {
    if (!user.value) return false;

    return (
        gate.allows('viewAny', 'Course') ||
        gate.allows('viewAny', 'CourseTemplate') ||
        user.value.isStudentSupport() ||
        gate.allows('viewAny', 'Meeting')
    );
});

const canManageFeatureFlags = computed(() => {
    return !!(
        user.value?.is_super_admin ||
        store.getters['User/hasRole']('Admin') ||
        store.getters['User/hasRole']('Owner')
    );
});

const { canSeeAdministrationDropdown: canSeeAdministrationFromPermissions } = useCanSeeAdministrationDropdown({
    user,
});
const canSeeAdministrationDropdown = computed(
    () =>
        canSeeAdministrationFromPermissions.value ||
        canManageLateSubmissionPolicies(user.value) ||
        canViewLateSubmissionDeductionsReport(user.value) ||
        (gate.allows('viewAny', 'ElearningPackage') && $hasFeature('ELEARNING_PACKAGES'))
);

const showHelpNavLink = computed(() => {
    if (store.getters['InstitutionSettings/get']('support_documentation_enabled')) {
        return true;
    }

    return gate.userCan('view all help articles');
});

const pageAuthorizedRoleIds = computed(() => {
    return Page.getPageAuthorizedRoleIds();
});

const showEditPageNav = computed(() => {
    if (!user.value) return false;

    if (store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner')) return true;

    if (!store.getters['InstitutionSettings/get']('page_enabled')) return false;

    const userGroupRoleIds = user.value.user_groups.map(group => group.role_id);

    return intersection(userGroupRoleIds, pageAuthorizedRoleIds.value).length > 0 || gate.allows('viewAny', 'Page');
});

const canSeeDashboardsNavLink = computed(() => {
    if (!user.value) return false;

    return (
        gate.userCan('view a dashboard') ||
        gate.userCan('view insight dashboard') ||
        gate.userCanViaGroup('view insight dashboard') ||
        user.value.isGroupInstructor()
    );
});

const canSeeProctorDashboardNavLink = computed(() => {
    return (
        gate.userCan("create another user's quiz attempt") || gate.userCanViaGroup("create another user's quiz attempt")
    );
});

const inboxEnabled = computed(() => {
    return $hasFeature('INBOX') && canViewInbox(user.value);
});

// Build menu model
const model = computed(() => {
    const menu = [];

    // Home section
    menu.push({
        label: 'Home',
        items: [
            {
                label: 'Dashboard',
                icon: 'pi pi-fw pi-home',
                to: '/',
                visible: true,
            },
        ],
    });

    // Courses section
    if (canSeeCourseDropdown.value) {
        const topLevelCourseItems = [
            {
                label: 'Course Codes',
                icon: 'pi pi-fw pi-tag',
                to: { name: 'course-codes' },
                visible: gate.allows('viewAny', 'CourseCode'),
            },
            {
                label: 'Courses',
                icon: 'pi pi-fw pi-graduation-cap',
                to: { name: 'courses' },
                visible: gate.allows('viewAny', 'Course'),
            },
            {
                label: 'Course Templates',
                icon: 'pi pi-fw pi-file',
                to: { name: 'course-templates' },
                visible: gate.allows('viewAny', 'CourseTemplate'),
            },
            {
                label: 'Due Dates and Schedules',
                icon: 'pi pi-fw pi-clock',
                to: { name: 'activities-view-upcoming' },
                visible: gate.allows('view', 'Activity') && gate.allows('viewAny', 'TimePeriod'),
            },
            {
                label: 'Meetings',
                icon: 'pi pi-fw pi-calendar',
                to: { name: 'meetings-index' },
                visible: gate.allows('viewAny', 'Meeting'),
            },
        ].sort((a, b) => a.label.localeCompare(b.label));

        const courseItems = [...topLevelCourseItems];

        menu.push({
            label: 'Courses',
            items: courseItems,
        });
    }

    // Administration section - main items and SuperAdmin section sorted alphabetically
    if (canSeeAdministrationDropdown.value) {
        const mainAdminItems = [
            {
                label: 'Activity Log',
                icon: 'pi pi-fw pi-history',
                to: { name: 'activity-log-index' },
                visible: gate.userCan('view the activity log'),
            },
            {
                label: 'Attempt Authorizations',
                icon: 'pi pi-fw pi-key',
                to: { name: 'attempt-authorizations-index' },
                visible: gate.allows('viewAny', 'AttemptAuthorization'),
            },
            {
                label: 'Dashboards',
                icon: 'pi pi-fw pi-th-large',
                to: { name: 'dashboards' },
                visible: canSeeDashboardsNavLink.value,
            },
            {
                label: 'Evaluation Templates',
                icon: 'pi pi-fw pi-check-square',
                to: { name: 'evaluation-templates-index' },
                visible: gate.allows('viewAny', 'EvaluationTemplate'),
            },
            {
                label: 'Feature Flags',
                icon: 'pi pi-fw pi-sliders-h',
                to: { name: 'feature-flags-index' },
                visible: canManageFeatureFlags.value,
            },
            {
                label: 'Integrations',
                icon: 'pi pi-fw pi-link',
                to: { name: 'integrations-index' },
                visible: gate.allows('viewAny', 'Integrations'),
            },
            {
                label: 'Groups',
                icon: 'pi pi-fw pi-sitemap',
                to: { name: 'groups-index' },
                visible: gate.allows('viewAny', 'Group'),
            },
            {
                label: 'Ebooks',
                icon: 'pi pi-fw pi-book',
                to: { name: 'ebooks-index' },
                visible: gate.allows('viewAny', 'Ebook'),
            },
            {
                label: 'Late Submission Policies',
                icon: 'pi pi-fw pi-clock',
                to: { name: 'late-submission-policies-index' },
                visible: canManageLateSubmissionPolicies(user.value),
            },
            {
                label: 'Attendance',
                icon: 'pi pi-fw pi-check-square',
                to: { name: 'attendance-scenario-index' },
                visible: gate.allows('viewAny', 'AttendanceScenario') || gate.allows('configure', 'AttendanceScenario'),
            },
            {
                label: 'Late Submission Deductions',
                icon: 'pi pi-fw pi-chart-bar',
                to: { name: 'late-submission-deductions-report' },
                visible: canViewLateSubmissionDeductionsReport(user.value),
            },
            {
                label: 'Locations',
                icon: 'pi pi-fw pi-map-marker',
                to: { name: 'locations-index' },
                visible: gate.allows('viewAny', 'Location'),
            },
            {
                label: 'Pages',
                icon: 'pi pi-fw pi-file',
                to: { name: 'edit-pages-index' },
                visible: showEditPageNav.value,
            },
            {
                label: 'People',
                icon: 'pi pi-fw pi-users',
                to: { name: 'people-index' },
                visible: gate.allows('viewAny', 'User'),
            },
            {
                label: 'Proctor Dashboard',
                icon: 'pi pi-fw pi-eye',
                to: { name: 'proctor-dashboard-quiz-lobby' },
                visible: canSeeProctorDashboardNavLink.value,
            },
            {
                label: 'Remediations',
                icon: 'pi pi-fw pi-refresh',
                to: { name: 'remediations-index' },
                visible: gate.allows('viewAny', 'Remediation'),
            },
            {
                label: 'Reports',
                icon: 'pi pi-fw pi-chart-line',
                to: { name: 'reports-index' },
                visible: gate.allows('viewAny', 'Report'),
            },
            {
                label: 'Risk Assessment Report',
                icon: 'pi pi-fw pi-exclamation-triangle',
                to: { name: 'risk-assessment-report' },
                visible: gate.userCan('view risk assessment report') && !gate.allows('viewAny', 'Report'),
            },
            {
                label: 'Rubric Templates',
                icon: 'pi pi-fw pi-table',
                to: { name: 'rubric-bank-index' },
                visible: gate.allows('viewAny', 'RubricBank'),
            },
            {
                label: 'Slide Decks',
                icon: 'pi pi-fw pi-images',
                to: { name: 'slide-decks-index' },
                visible: gate.allows('viewAny', 'SlideDeck'),
            },
            {
                label: 'Surveys',
                icon: 'pi pi-fw pi-chart-bar',
                to: { name: 'surveys-index' },
                visible: gate.allows('viewAny', 'Quiz'),
            },
            {
                label: 'Videos',
                icon: 'pi pi-fw pi-video',
                to: { name: 'videos-index' },
                visible: gate.allows('viewAny', 'Video'),
            },
            {
                label: 'Quizzes',
                icon: 'pi pi-fw pi-question-circle',
                to: { name: 'quizzes-index' },
                visible: gate.allows('viewAny', 'Quiz'),
            },
            {
                label: 'Content Library',
                icon: 'pi pi-fw pi-folder',
                items: [
                    {
                        label: 'eLearning Packages',
                        icon: 'pi pi-fw pi-file',
                        to: { name: 'elearning-packages-index' },
                        visible: gate.allows('viewAny', 'ElearningPackage') && $hasFeature('ELEARNING_PACKAGES'),
                    },
                ],
                visible: gate.allows('viewAny', 'ElearningPackage') && $hasFeature('ELEARNING_PACKAGES'),
            },
        ].sort((a, b) => a.label.localeCompare(b.label));

        const superAdminItems = [
            {
                label: 'Horizon',
                icon: 'pi pi-fw pi-server',
                url: '/horizon',
                target: '_blank',
                visible: gate.userCan('view horizon'),
            },
            {
                label: 'Pulse',
                icon: 'pi pi-fw pi-heart',
                url: '/pulse',
                target: '_blank',
                visible: gate.userCan('view pulse'),
            },
        ].sort((a, b) => a.label.localeCompare(b.label));

        menu.push({
            label: 'Administration',
            items: [...mainAdminItems, { separator: true }, ...superAdminItems],
        });
    }

    // Resources section - sorted alphabetically
    if (user.value) {
        const resourceItems = [
            {
                label: 'Help & Support',
                icon: 'pi pi-fw pi-question-circle',
                to: { name: 'help-home' },
                visible: showHelpNavLink.value,
            },
            {
                label: 'Inbox',
                icon: 'pi pi-fw pi-inbox',
                to: { name: 'inbox' },
                visible: inboxEnabled.value,
            },
            {
                label: 'My Notes',
                icon: 'pi pi-fw pi-pencil',
                to: { name: 'notes-home' },
                visible: true,
            },
        ].sort((a, b) => a.label.localeCompare(b.label));

        menu.push({
            label: 'Resources',
            items: resourceItems,
        });
    }

    return menu;
});
</script>

<style lang="scss" scoped></style>
